import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';

// assignedUserIds may arrive populated (user objects) or as plain id strings.
const assignedEntryId = entry => String(typeof entry === 'object' ? entry?._id : entry);

const sortCategoriesByName = list =>
  [...list].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'de'));

/**
 * Management view for the IT specialty categories ("Spezialgebiete"): create,
 * rename, (de-)activate categories and assign the specialists that the SMS
 * escalation chain contacts first.
 */
export default function IssueCategoriesView() {
  const authCtx = useContext(AuthContext);
  const { getCategories, createCategory, updateCategory, deleteCategory, getEligibleSpecialists } =
    useIssue();

  const canConfigure = authCtx.checkRight('issue:configure');

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const createNameRef = useRef(null);
  const createDescriptionRef = useRef(null);
  const editNameRef = useRef(null);

  const load = useCallback(async () => {
    // Assignable specialists (IT roles only) are optional reference data; the
    // list still renders without them.
    const [categoryRes, userRes] = await Promise.all([
      getCategories(),
      getEligibleSpecialists().catch(() => null),
    ]);
    setCategories(Array.isArray(categoryRes?.categories) ? categoryRes.categories : []);
    setUsers(Array.isArray(userRes?.users) ? userRes.users : []);
  }, [getCategories, getEligibleSpecialists]);

  useEffect(() => {
    if (!canConfigure) return;
    let cancelled = false;
    const initialLoad = async () => {
      setLoading(true);
      try {
        await load();
      } catch (err) {
        // The hook already showed a German error toast.
        console.error('specialty categories load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    initialLoad();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canConfigure]);

  const activeUsers = useMemo(() => users.filter(user => user.isActive !== false), [users]);

  const usersById = useMemo(
    () => new Map(activeUsers.map(user => [String(user._id), user])),
    [activeUsers]
  );

  // Map a category's assignedUserIds to user objects for the Autocomplete;
  // populated entries whose user is not in the active list are kept as-is.
  const assignedUsersOf = useCallback(
    category =>
      (category.assignedUserIds || [])
        .map(entry => {
          const id = assignedEntryId(entry);
          return usersById.get(id) || (typeof entry === 'object' && entry ? entry : null);
        })
        .filter(Boolean),
    [usersById]
  );

  const patchCategoryLocally = useCallback((categoryId, patch) => {
    setCategories(prev =>
      prev.map(category => (category._id === categoryId ? { ...category, ...patch } : category))
    );
  }, []);

  const handleCreate = useCallback(async () => {
    const name = createNameRef.current?.value?.trim() || '';
    if (!name) {
      toast.error('Bitte einen Namen für das Spezialgebiet angeben');
      return;
    }
    const description = createDescriptionRef.current?.value?.trim() || '';
    setCreating(true);
    try {
      const result = await createCategory({ name, ...(description ? { description } : {}) });
      // A null result means the request was stored for auth escalation and has
      // not been executed yet - nothing to show in the list at this point.
      if (result?.category) {
        setCategories(prev => sortCategoriesByName([...prev, result.category]));
        toast.success('Spezialgebiet angelegt');
        if (createNameRef.current) createNameRef.current.value = '';
        if (createDescriptionRef.current) createDescriptionRef.current.value = '';
      }
    } catch (err) {
      if (err?.statusCode === 409 || err?.code === 'ISSUE_CATEGORY_EXISTS') {
        toast.error('Spezialgebiet existiert bereits');
      }
    } finally {
      setCreating(false);
    }
  }, [createCategory, load]);

  const handleToggleActive = useCallback(
    async (category, active) => {
      const previous = category.active;
      patchCategoryLocally(category._id, { active });
      try {
        await updateCategory(category._id, { active });
      } catch (err) {
        // Revert the optimistic update; the hook already showed a toast.
        patchCategoryLocally(category._id, { active: previous });
        console.error('specialty category toggle failed', err);
      }
    },
    [updateCategory, patchCategoryLocally]
  );

  const handleAssignChange = useCallback(
    async (category, newUsers) => {
      const previous = category.assignedUserIds || [];
      patchCategoryLocally(category._id, { assignedUserIds: newUsers });
      try {
        const result = await updateCategory(category._id, {
          assignedUserIds: newUsers.map(user => String(user._id)),
        });
        if (result?.category) patchCategoryLocally(category._id, result.category);
      } catch (err) {
        // Revert the optimistic update; the hook already showed a toast.
        patchCategoryLocally(category._id, { assignedUserIds: previous });
        console.error('specialty assignment failed', err);
      }
    },
    [updateCategory, patchCategoryLocally]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteCategory(deleteTarget._id);
      // A null result means the request was stored for auth escalation.
      if (result?.deleted) {
        setCategories(prev => prev.filter(category => category._id !== deleteTarget._id));
        toast.success('Spezialgebiet gelöscht');
      }
      setDeleteTarget(null);
    } catch (err) {
      // The hook already showed a German error toast.
      console.error('specialty delete failed', err);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleteCategory]);

  const handleRename = useCallback(async () => {
    if (!editTarget) return;
    const name = editNameRef.current?.value?.trim() || '';
    if (!name) {
      toast.error('Bitte einen Namen angeben');
      return;
    }
    setSavingEdit(true);
    try {
      const result = await updateCategory(editTarget._id, { name });
      patchCategoryLocally(editTarget._id, result?.category || { name });
      toast.success('Spezialgebiet aktualisiert');
      setEditTarget(null);
    } catch (err) {
      // The hook already showed a German error toast.
      console.error('specialty rename failed', err);
    } finally {
      setSavingEdit(false);
    }
  }, [editTarget, updateCategory, patchCategoryLocally]);

  if (!canConfigure) return <Navigate to='/' replace />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant='h5' sx={{ fontWeight: 700 }}>
        Spezialgebiete der IT
      </Typography>
      <Typography variant='caption' color='text.secondary' component='p' sx={{ mb: 3 }}>
        Kategorien für Issues – die SMS-Eskalationskette kontaktiert zuerst die zugewiesenen
        Spezialisten.
      </Typography>

      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField inputRef={createNameRef} label='Name' size='small' sx={{ flexGrow: 1 }} />
          <TextField
            inputRef={createDescriptionRef}
            label='Beschreibung (optional)'
            size='small'
            sx={{ flexGrow: 2 }}
          />
          <Button
            variant='contained'
            onClick={handleCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Anlegen
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map(index => (
            <Skeleton key={index} variant='rounded' height={120} />
          ))}
        </Stack>
      ) : categories.length === 0 ? (
        <Typography color='text.secondary'>Noch keine Spezialgebiete angelegt.</Typography>
      ) : (
        <Stack spacing={2}>
          {categories.map(category => {
            const assigned = assignedUsersOf(category);
            return (
              <Paper key={category._id} variant='outlined' sx={{ p: 2 }}>
                <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant='subtitle1'
                      sx={{
                        fontWeight: 700,
                        ...(category.active === false && {
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                        }),
                      }}
                    >
                      {category.name}
                    </Typography>
                    {category.description && (
                      <Typography variant='body2' color='text.secondary'>
                        {category.description}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title='Umbenennen'>
                    <IconButton size='small' onClick={() => setEditTarget(category)}>
                      <EditIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Löschen'>
                    <IconButton size='small' onClick={() => setDeleteTarget(category)}>
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={category.active === false ? 'Inaktiv' : 'Aktiv'}>
                    <Switch
                      checked={category.active !== false}
                      onChange={event => handleToggleActive(category, event.target.checked)}
                    />
                  </Tooltip>
                </Stack>
                <Autocomplete
                  multiple
                  options={activeUsers}
                  value={assigned}
                  getOptionLabel={user => user.username || ''}
                  isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
                  filterSelectedOptions
                  onChange={(event, newUsers) => handleAssignChange(category, newUsers)}
                  renderInput={params => (
                    <TextField {...params} label='Spezialisten' size='small' />
                  )}
                />
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {assigned.length === 1
                    ? '1 Spezialist zugewiesen'
                    : `${assigned.length} Spezialisten zugewiesen`}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Spezialgebiet umbenennen</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={editNameRef}
            label='Name'
            fullWidth
            autoFocus
            defaultValue={editTarget?.name || ''}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditTarget(null)} disabled={savingEdit}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleRename}
            disabled={savingEdit}
            startIcon={savingEdit ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Spezialgebiet löschen</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Soll das Spezialgebiet „{deleteTarget?.name}“ wirklich gelöscht werden? Bestehende
            Issues behalten ihre Historie, werden aber keinem Spezialgebiet mehr zugeordnet.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
